import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {navigate, SiteRoutes, useRoute} from '../../../lib/routes';
import {cls} from '../../utils';
import {Button} from '../inputs/button';
import {useTouch, SwipeDirection} from './use-touch';

import css from './swipe.module.css';

export type Page = {
	route: keyof SiteRoutes;
	routeParams?: SiteRoutes[keyof SiteRoutes];
	component: React.FC<any>;
	title: React.ReactNode;
	affectedRoutes: Array<keyof SiteRoutes>;
	props?: Record<string, any>;
};

type Props = {
	pages: Page[];
};

export const Swipe: React.FC<Props> = ({pages}) => {
	const router = useRoute();
	const routeName = router.routeName as keyof SiteRoutes;
	const routeParams = router.query;
	const url = router.asPath;

	const [currentPage, setCurrentPage] = useState<Page>(
		pages.find((page) => page.affectedRoutes.includes(routeName)) || pages[0],
	);

	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const newPage = pages.find((page) => {
			return page.affectedRoutes.includes(routeName);
		});
		if (newPage) {
			setCurrentPage(newPage);
			let menuItem: HTMLDivElement | null = null;
			for (let i = 0; i < (menuRef.current?.children.length || 0); i++) {
				const item = menuRef.current?.children[i] as HTMLDivElement;
				if (item && item.dataset.route === newPage.route) {
					menuItem = item;
					break;
				}
			}

			if (menuItem) {
				menuItem.scrollIntoView({behavior: 'smooth'});
			}
		}
	}, [routeName, url, pages]);

	const currentPageIndex = useMemo(
		() => pages.findIndex((page) => page.route === currentPage.route),
		[currentPage, pages],
	);

	const LeftComponent = pages[currentPageIndex - 1]?.component;
	const CenterComponent = currentPage.component;
	const RightComponent = pages[currentPageIndex + 1]?.component;

	const [renderLeft, setRenderLeft] = useState(false);
	const [renderRight, setRenderRight] = useState(false);

	const swipeRef = useRef<HTMLDivElement>(null);
	const leftPageRef = useRef<HTMLDivElement>(null);
	const centerPageRef = useRef<HTMLDivElement>(null);
	const rightPageRef = useRef<HTMLDivElement>(null);

	const resetPages = useCallback(() => {
		for (const ref of [leftPageRef, centerPageRef, rightPageRef]) {
			if (!ref.current) continue;
			ref.current.style.transform = '';
			ref.current.classList.remove('animating');
		}
	}, []);

	const handleChangePage = useCallback(
		(direction: SwipeDirection | null) => {
			if (!direction) return;
			const page = direction === SwipeDirection.Left ? pages[currentPageIndex - 1] : pages[currentPageIndex + 1];
			navigate(page.route, page.routeParams);
			setCurrentPage(page);
			setRenderLeft(false);
			setRenderRight(false);
		},
		[currentPageIndex, pages],
	);

	// страницы стоят сдвинутыми, пока идёт анимация и пока не отрисована новая: снимаем сдвиг
	// в том же кадре, в котором она появилась
	useLayoutEffect(resetPages, [currentPage, resetPages]);

	const handleStartRender = useCallback((direction: SwipeDirection) => {
		if (direction === SwipeDirection.Right) setRenderRight(true);
		else setRenderLeft(true);
	}, []);

	useTouch({
		swipeAreaRef: swipeRef,
		leftPage: currentPageIndex > 0,
		rightPage: currentPageIndex < pages.length - 1,
		onChange: handleChangePage,
		startRender: handleStartRender,
		reset: resetPages,
		leftPageRef,
		rightPageRef,
		centerPageRef,
	});

	return (
		<div className={css.wrapper}>
			<div className={css.menu} ref={menuRef}>
				{pages.map((page) => (
					<Button
						appearance="subtle"
						data-route={page.route}
						route={page.route}
						routeParams={page.routeParams}
						key={`${page.route}${JSON.stringify(page.routeParams)}`}
						onClick={() => setCurrentPage(page)}
						className={cls(css.menuItem, page.route === currentPage.route && css.current)}
					>
						{page.title}
					</Button>
				))}
			</div>
			<div className={css.swipeWrapper} ref={swipeRef}>
				<div className={cls(css.content, css.pageLeft)} ref={leftPageRef}>
					{renderLeft && LeftComponent && <LeftComponent {...pages[currentPageIndex - 1].props} />}
				</div>
				<div className={cls(css.content, css.pageCenter)} ref={centerPageRef}>
					<CenterComponent {...currentPage.props} />
				</div>
				<div className={cls(css.content, css.pageRight)} ref={rightPageRef}>
					{renderRight && RightComponent && <RightComponent {...pages[currentPageIndex + 1].props} />}
				</div>
			</div>
		</div>
	);
};
